import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    getAdsDetail,
    getEngage,
    postEngageAction,
    engageGetCredit,
    createPayGetCoupon,
    validateCampaignEngagedLimit
} from "../../../services/ads/ads.service";
import { links } from "../../../constants/links";
import { ENGAGE_GET_CREDIT_SESSION } from "../../../constants";
import { InitialData } from "../types";

export const useEngage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const id = useMemo(() => location.pathname.split("/")[2], [location.pathname]);
    const [errorMessage, setErrorMessage] = useState("");
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(!!((location?.state as any)?.isSkipCampaignPage || (location?.state as any)?.isFromVerifyToken));
    const [isDetailDataFetch, setIsDetailDataFetch] = useState(false);

    const hasEngaged = useRef(false);
    const engageSessionIdRef = useRef(localStorage.getItem("engage-session-id") || "");
    const hasFetchedDetail = useRef(false);

    const {
        isGoogleForm = false,
        isPayGetCoupon = false,
        isSkipCampaignPage = false,
        channelSessionId = "",
        subcampaignData = "",
        isFromVerifyToken = false,
        status: locationStatus = "INITIATED",
        isEngageGetCredit: locationIsEngageGetCredit = false,
        isNonVendingPayGetCoupon = false,
        productPrice: initialProductPrice = 0,
        isProductPriceEditable = false
    } = (location?.state || {}) as any;

    const [isEngageGetCredit] = useState<boolean>(locationIsEngageGetCredit);
    const [productPrice, setProductPrice] = useState<number>(initialProductPrice);

    const session_id = localStorage.getItem("session-id") || "";
    const qr_session = localStorage.getItem("qrscanlog_id") || "";

    const initialData: InitialData = useMemo(() => ({
        languages: subcampaignData?.languages || {},
        status: "",
        is_skip_detail_page: false,
        url: subcampaignData?.advertiser_url || "",
        id: parseInt(id) || 0,
        is_click_campaign: false,
        customer_id: 0,
        expired_at: "",
        advertiser_banner: subcampaignData?.background || "",
        subcampaign_type_name: "",
        download_app_3rd_party: "",
        is_multi_use: subcampaignData?.is_multi_use || false,
        stop_engage: subcampaignData?.stop_engage || false,
        coupon_code: "",
        is_3rdparty_coupon_use: false,
        urlParamName: subcampaignData?.url_param_name,
        banner_image_corner: subcampaignData?.banner_image_corner,
        custom_html: subcampaignData?.custom_html,
    }), [id, subcampaignData]);

    const [data, setData] = useState<InitialData>(initialData);

    const skipCampaignPage = isFromVerifyToken || isSkipCampaignPage;

    useEffect(() => {
        if (skipCampaignPage) {
            setIsLoading(true);
        }
    }, [skipCampaignPage]);

    useEffect(() => {
        if (isEngageGetCredit && subcampaignData && !isFromVerifyToken) {
            setIsLoading(false);
        }
    }, [isEngageGetCredit, subcampaignData, isFromVerifyToken]);

    const engageGetCreditFn = useCallback(() => {
        engageGetCredit({
            subcampaign_id: data?.id,
            qr_session: qr_session,
            customer_session: session_id,
            channelSessionId: channelSessionId
        }).then((res: any) => {
            const engageGetCreditSession = res?.data?.engage_get_credit_session;
            localStorage.setItem(ENGAGE_GET_CREDIT_SESSION, engageGetCreditSession);

            const redirectURL = new URL(data.url);
            if (data.urlParamName !== undefined) {
                redirectURL.searchParams.set(data.urlParamName, engageGetCreditSession);
            } else {
                redirectURL.pathname = `${redirectURL.pathname.replace(/\/$/,"")}/${engageGetCreditSession}`;
            }
            window.location.href = redirectURL.toString();
        }).catch((error: string) => {
            console.error("Error creating engageGetCredit session", error);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [data, qr_session, session_id, channelSessionId]);


    const createPayGetCouponFn = useCallback(() => {
        createPayGetCoupon({
            subcampaignId: data?.id,
            engageSessionId: engageSessionIdRef.current,
            productPrice: productPrice,
            qrscanlogId: qr_session,
            session_id: session_id,
            channelSessionId: channelSessionId
        }).then((res: any) => {
            const redirectURL = res.data.scan_url;
            window.location.href = redirectURL;
            return;
        }).catch((error: string) => {
            console.error("Error creating paygetcoupon", error);
            setIsLoading(false);
        }).finally(() => {
            // setIsLoading(false) is called in catch, and if successful it redirects
        })
    }, [data, qr_session, session_id, channelSessionId, productPrice])

    const postEngageActionFn = useCallback((
        subId: number,
        url: string,
        isClickCampaign: boolean,
        customerId: number,
        isFromContinueBtn?: boolean
    ) => {
        postEngageAction({
            subcampaign_id: subId,
            qrscanlog_id: localStorage.getItem("qrscanlog_id") || "",
            isFromContinueBtn: isFromContinueBtn,
            channelSessionId: channelSessionId
        }).then(async (res: any) => {
            hasEngaged.current = true;
            setButtonDisabled(true);

            if (res.status === 200) {
                const newEngageSessionId = res.data.engage_session_id;
                engageSessionIdRef.current = newEngageSessionId;

                const isClickType = isClickCampaign || isPayGetCoupon || data.subcampaign_type_name === "SMART_ENGAGE_CLICK";
                if (isClickType) {
                    if (isGoogleForm || isPayGetCoupon) {
                        return navigate(links.USE_REWARD.replace(":id", String(subId)));
                    }

                    if (newEngageSessionId && url) {
                        getEngage(newEngageSessionId).then((engageRes) => {
                            if (engageRes) {
                                setButtonDisabled(false);
                                window.location.href = url;
                            }
                        }).catch(() => {
                            return navigate(`${links.SMART_RETAIL_ERROR}?error-msg=coupon_error`);
                        }).finally(() => {
                            setButtonDisabled(false);
                            setIsLoading(false);
                        });
                    } else {
                        setIsLoading(false);
                    }
                } else if (
                    data.subcampaign_type_name === "SMART_ENGAGE_DOWNLOAD_APP" &&
                    data.download_app_3rd_party === "SR"
                ) {
                    navigate(links.DAGC.replace(":id", String(subId)), { state: { subcampaign_id: subId } });
                } else {
                    setButtonDisabled(false);
                    window.location.href = url;
                }
            } else {
                window.location.reload();
            }
        }).catch((error: string) => {
            console.error("Error postEngageAction", error);
            setIsLoading(false);
        });
    }, [channelSessionId, data.subcampaign_type_name, data.download_app_3rd_party, isGoogleForm, isPayGetCoupon, navigate]);

    const getYouGift = useCallback((
        subId: number,
        url: string,
        isClickCampaign: boolean,
        customerId: number,
        isFromContinueBtn?: boolean
    ) => {
        if (buttonDisabled) return;

        hasEngaged.current = true;

        if (data?.is_multi_use === false && locationStatus !== "INITIATED" && data?.stop_engage === true) {
            navigate(links.ENGAGED_PAGE, {
                state: {
                    subcampaign_id: subId,
                    advertiser_banner: data?.advertiser_banner,
                    is_3rdparty_coupon_use: data?.is_3rdparty_coupon_use,
                    coupon_code: data?.coupon_code || data?.url,
                    banner_image_corner: data?.banner_image_corner,
                }
            });
            return;
        }

        validateCampaignEngagedLimit({ id: subId }).then(async (statusResponse: any) => {
            if (statusResponse === 200) {
                if (isEngageGetCredit && data.url) {
                    engageGetCreditFn();
                }
                else if (isNonVendingPayGetCoupon) {
                    createPayGetCouponFn();
                }
                else {
                    postEngageActionFn(subId, url, isClickCampaign, customerId, isFromContinueBtn);
                }
            } else {
                setIsLoading(false);
            }
        }).catch((error: any) => {
            const errorCode = error.response?.data?.error_code;
            const limitType = error.response?.data?.limit_engage_type;

            if (errorCode === "engaged_limit_exceeded") {
                navigate(links.CAMPAIGN_LIMIT_ERROR, {
                    state: {
                        advertiser_banner: data?.advertiser_banner,
                        banner_image_corner: data?.banner_image_corner,
                        limit_engage_type: limitType,
                    }
                });
            } else {
                setIsLoading(false);
            }
        });
    }, [buttonDisabled, data, locationStatus, isEngageGetCredit, engageGetCreditFn, postEngageActionFn, navigate, createPayGetCouponFn, isNonVendingPayGetCoupon]);

    const getAdsDetailData = useCallback(async () => {
        setIsLoading(true);
        try {
            const fetchStatus = isPayGetCoupon ? locationStatus : "INITIATED";
            const detailData = await getAdsDetail({
                id: id,
                type: fetchStatus,
                engage_id: engageSessionIdRef.current
            });

            setIsDetailDataFetch(true);
            setData((prevData) => ({
                ...prevData,
                ...detailData
            }));

            if (detailData?.error_msg) {
                const message_key = detailData.is_expired_or_finished_issued
                    ? "engagedOrExpired"
                    : "isNotAvailable";
                setErrorMessage(message_key);
            }
        } catch (error: any) {
            const errorData = error?.response?.data;
            const errorMsg = errorData?.error_msg;
            const errorCode = errorData?.error_code;

            if (errorMsg === "CAMPAIGN_ALREADY_EXIST") {
                return navigate(links.ENGAGED_PAGE, {
                    state: {
                        subcampaign_id: errorData?.subcampaign_id,
                        advertiser_banner: errorData?.advertiser_banner,
                        last_engaged_session: errorData?.last_engaged_session,
                        banner_image_corner: errorData?.banner_image_corner,
                    }
                });
            }

            if (errorCode === "engaged_limit_exceeded") {
                return navigate(links.CAMPAIGN_LIMIT_ERROR, {
                    state: {
                        advertiser_banner: errorData?.background,
                        banner_image_corner: errorData?.banner_image_corner,
                        limit_engage_type: errorData?.limit_engage_type
                    }
                });
            }

            setErrorMessage("isNotAvailable");
            setIsLoading(false);
        } finally {
            if (!hasEngaged.current) {
                setIsLoading(false);
            }
        }
    }, [id, isPayGetCoupon, locationStatus, navigate, isFromVerifyToken, isEngageGetCredit, isSkipCampaignPage]);

    const goToRewardList = useCallback(() => {
        navigate(links.EARN_PAGE);
    }, [navigate]);

    useEffect(() => {
        const reloadOnBack = () => {
            const hasReloaded = localStorage.getItem("hasReloadedOnce");
            if (!hasReloaded) {
                localStorage.setItem("hasReloadedOnce", "true");
                window.location.reload();
            } else {
                localStorage.removeItem("hasReloadedOnce");
            }
        };

        window.addEventListener("pageshow", reloadOnBack);
        return () => window.removeEventListener("pageshow", reloadOnBack);
    }, []);

    useEffect(() => {
        if (session_id && !hasFetchedDetail.current) {
            hasFetchedDetail.current = true;
            getAdsDetailData();
        } else {
            setIsLoading(false);
        }
    }, [session_id, getAdsDetailData]);

    useEffect(() => {
        if (!hasEngaged.current && isDetailDataFetch && skipCampaignPage && data?.url) {
            hasEngaged.current = true;
            getYouGift(
                data?.id,
                data?.url,
                data?.is_click_campaign,
                data?.customer_id
            );
        }
    }, [isDetailDataFetch, getYouGift, skipCampaignPage, data?.url]);

    useEffect(() => {
        if (isSkipCampaignPage && data && !hasEngaged.current) {
            hasEngaged.current = true;
            getYouGift(
                data?.id,
                data?.url,
                data?.is_click_campaign || data.subcampaign_type_name === "SMART_ENGAGE_CLICK",
                data?.customer_id,
                true
            );
        }
    }, [data, isSkipCampaignPage, getYouGift]);

    useEffect(() => {
        if (!isProductPriceEditable) return;
        setButtonDisabled(productPrice <= 0);
    }, [productPrice, isProductPriceEditable]);

    return {
        data,
        errorMessage,
        buttonDisabled,
        isLoading,
        isSkipCampaignPage,
        getYouGift,
        goToRewardList,
        isNonVendingPayGetCoupon,
        productPrice,
        setProductPrice,
        isProductPriceEditable,
        skipCampaignPage
    };
};
